import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Space, Card, Statistic, Row, Col, Popconfirm,
  message, Divider, Modal, Form, Typography, Input,
} from 'antd';
import { StopOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const STATUS_LABELS = {
  pending: { label: '呼叫中', color: 'orange' },
  ongoing: { label: '通话中', color: 'green' },
  ended: { label: '已结束', color: 'default' },
  rejected: { label: '已拒绝', color: 'red' },
  busy: { label: '对方忙', color: 'gold' },
  missed: { label: '未接听', color: 'gray' },
  cancelled: { label: '已取消', color: 'default' },
};

export default function CallsPage({ scope, type }) {
  const title = (scope === 'group' ? '群' : '') + (type === 'audio' ? '语音' : '视频') + '通话';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, ongoing: 0, ended: 0, failed: 0, totalDuration: 0 });
  const [bans, setBans] = useState([]);
  const [banModal, setBanModal] = useState(false);
  const [banForm] = Form.useForm();

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const [{ list, total: t }, s] = await Promise.all([
        api.get('/admin/calls', { params: { scope, type, page: p, limit: 50 } }),
        api.get('/admin/calls/stats', { params: { scope, type } }),
      ]);
      setRows(list);
      setTotal(t);
      setPage(p);
      setStats(s || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [scope, type]);

  const loadBans = async () => {
    try { setBans(await api.get('/admin/call-bans')); } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(1); loadBans(); }, [load]);

  const handleTerminate = async (callId) => {
    try {
      await api.post(`/admin/calls/${callId}/terminate`);
      message.success('已强制结束');
      load(page);
    } catch (e) { message.error((e && e.error) || '操作失败'); }
  };

  const handleAddBan = async () => {
    const v = await banForm.validateFields();
    try {
      await api.post('/admin/call-bans', v);
      message.success('已禁止该用户发起通话');
      setBanModal(false); banForm.resetFields(); loadBans();
    } catch (e) { message.error((e && e.error) || '操作失败'); }
  };

  const handleRemoveBan = async (userId) => {
    try { await api.delete(`/admin/call-bans/${userId}`); message.success('已解除'); loadBans(); }
    catch (e) { message.error((e && e.error) || '操作失败'); }
  };

  const columns = [
    { title: 'ID', width: 70, dataIndex: 'id' },
    {
      title: scope === 'group' ? '发起方' : '主叫', width: 140,
      render: (_, r) => <Text>{r.caller_name || r.caller_id}{r.caller_phone ? ` (${r.caller_phone})` : ''}</Text>,
    },
    {
      title: scope === 'group' ? '群聊' : '被叫', width: 140,
      render: (_, r) => <Text>{scope === 'group' ? (r.group_name || r.group_id) : (r.callee_name || r.callee_id || '-')}</Text>,
    },
    { title: '类型', width: 80, render: () => <Tag>{type === 'audio' ? '语音' : '视频'}</Tag> },
    {
      title: '状态', width: 100,
      render: (_, r) => { const s = STATUS_LABELS[r.status] || { label: r.status, color: 'default' }; return <Tag color={s.color}>{s.label}</Tag>; },
    },
    { title: '发起时间', width: 150, dataIndex: 'start_time', render: (v) => v ? new Date(v).toLocaleString() : '-' },
    { title: '时长(秒)', width: 90, dataIndex: 'duration', render: (v) => (v == null ? '-' : v) },
    {
      title: '操作', width: 100,
      render: (_, r) => (r.status === 'ongoing' || r.status === 'pending') ? (
        <Popconfirm title="强制结束该通话?" onConfirm={() => handleTerminate(r.call_id)} icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}>
          <Button size="small" danger icon={<StopOutlined />}>结束</Button>
        </Popconfirm>
      ) : <Text type="secondary">-</Text>,
    },
  ];

  const banColumns = [
    { title: '用户ID', width: 90, dataIndex: 'user_id' },
    { title: '昵称', width: 120, render: (_, r) => r.nickname || '-' },
    { title: '手机号', width: 130, dataIndex: 'phone' },
    { title: '原因', dataIndex: 'reason' },
    {
      title: '操作', width: 90,
      render: (_, r) => (
        <Popconfirm title="解除禁呼?" onConfirm={() => handleRemoveBan(r.user_id)}>
          <Button size="small" danger>解除</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>{title}管理</Title>
        <Space>
          <Button onClick={() => load(page)}>刷新</Button>
          <Button type="primary" danger icon={<StopOutlined />} onClick={() => setBanModal(true)}>禁呼管理</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="总通话数" value={stats.total || 0} /></Card></Col>
        <Col span={6}><Card><Statistic title="进行中" value={stats.ongoing || 0} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="已结束" value={stats.ended || 0} /></Card></Col>
        <Col span={6}><Card><Statistic title="总时长(秒)" value={stats.totalDuration || 0} /></Card></Col>
      </Row>

      <Table columns={columns} dataSource={rows} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 50, onChange: load, showSizeChanger: false }} size="small" />

      <Divider />

      <Card title="禁呼用户（禁止发起通话）" size="small">
        <Table columns={banColumns} dataSource={bans} rowKey="user_id" pagination={false} size="small" />
      </Card>

      <Modal title="添加禁呼用户" open={banModal} onOk={handleAddBan} onCancel={() => setBanModal(false)}>
        <Form form={banForm} layout="vertical">
          <Form.Item name="userId" label="用户ID" rules={[{ required: true, message: '请输入用户ID' }]}>
            <Input type="number" placeholder="要禁止发起通话的用户ID" />
          </Form.Item>
          <Form.Item name="reason" label="原因"><Input.TextArea rows={3} placeholder="禁呼原因（可选）" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
